const moneyFormat = (cents) => {
  const format = window.themeVariables?.settings?.moneyFormat;
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, format);
  }
  const amount = (cents / 100).toFixed(2);
  return (format || '$ {{amount}}').replace('{{amount}}', amount);
};

const getImageSrc = (image) => {
  if (!image) return '';
  if (typeof image === 'string') return image;
  return (
    image.src ||
    image.url ||
    (image.preview_image && (image.preview_image.src || image.preview_image.url)) ||
    ''
  );
};

const productImageSrc = (product, variant) => {
  return (
    getImageSrc(variant?.featured_image) ||
    getImageSrc(variant?.image) ||
    getImageSrc(product?.featured_image) ||
    (product?.images && getImageSrc(product.images[0])) ||
    getImageSrc(product?.featured_media?.preview_image) ||
    ''
  );
};

const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

class CartDrawerInternal {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('.cart-drawer__panel');
    this.overlay = root.querySelector('.cart-drawer__overlay');
    this.itemsContainer = root.querySelector('#CartDrawerItems');
    this.subtotalEl = root.querySelector('[data-cart-subtotal]');
    this.messages = root.querySelector('.cart-drawer__messages');
    this.addOnContainer = root.querySelector('#CartDrawerAddOn');
    this.emptyContainer = root.querySelector('#CartDrawerEmpty');
    this.emptyUpsellBlock = root.querySelector('#CartDrawerEmptyUpsell');
    this.emptyUpsellContainer = root.querySelector('#CartDrawerEmptyUpsellProductContainer');
    const addOnScript = root.querySelector('#CartDrawerAddOnProducts');
    this.addOnData = addOnScript ? JSON.parse(addOnScript.textContent) : null;
    const emptyUpsellScript = root.querySelector('#CartDrawerEmptyUpsellProduct');
    this.emptyUpsellData = emptyUpsellScript ? JSON.parse(emptyUpsellScript.textContent) : null;

    this.overlay.addEventListener('click', () => this.close());
    const closeBtn = root.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    const checkoutBtn = root.querySelector('.cart-drawer__checkout');
    if (checkoutBtn) checkoutBtn.addEventListener('click', () => {
      document.dispatchEvent(new Event('cartDrawer:checkoutClick'));
    });

    this.onKeyDown = this.onKeyDown.bind(this);
  }

  open(cart) {
    this.activeElement = document.activeElement;
    this.render(cart);
    this.root.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    document.addEventListener('keydown', this.onKeyDown);
    document.dispatchEvent(new CustomEvent('cartDrawer:open', { detail: cart }));
    document.dispatchEvent(new CustomEvent('cartDrawer:change', { detail: cart }));
  }

  close() {
    this.root.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.activeElement) this.activeElement.focus();
    document.dispatchEvent(new Event('cartDrawer:close'));
    this.stopLoading();
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
    if (e.key === 'Tab') {
      const focusables = this.panel.querySelectorAll('a[href], button:not([disabled]), textarea, input, select');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  render(cart) {
    this.currentCart = cart;
    if (cart.items.length === 0) {
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = '';
        this.itemsContainer.hidden = true;
      }
      if (this.emptyContainer) {
        this.emptyContainer.hidden = false;
        this.renderEmptyUpsell();
      }
      if (this.addOnContainer) this.addOnContainer.hidden = true;
    } else {
      if (this.itemsContainer) {
        this.itemsContainer.hidden = false;
        this.itemsContainer.innerHTML = cart.items.map((item, index) => this.renderLineItem(item, index + 1)).join('');
      }
      if (this.emptyContainer) this.emptyContainer.hidden = true;
      this.bindLineItemEvents();
      this.renderAddOn(cart);
    }
    this.subtotalEl.textContent = moneyFormat(cart.total_price);
  }

  renderLineItem(item, line) {
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__variant">${item.variant_title}</p>` : '';
    const properties = Object.entries(item.properties || {})
      .filter(([k, v]) => v && k.indexOf('_') !== 0)
      .map(([k, v]) => `<p class="cart-drawer__property">${k}: ${v}</p>`)
      .join('');
    const discounts = (item.discount_allocations || [])
      .map((d) => `<span class="cart-drawer__discount">${d.discount_application?.title || ''}</span>`)
      .join('');
    const price = moneyFormat(item.final_price);
    const original = item.original_price > item.final_price ? `<span class="cart-drawer__price-original">${moneyFormat(item.original_price)}</span>` : '';

    let step = 1;

    if (window.customer_has_price_list && window.bulk_quantities[item.variant_id]) {
      step = parseInt(window.bulk_quantities[item.variant_id]) || 1;
    }

    return `
      <div class="cart-drawer__line-item" data-line="${line}">
        <button type="button" class="remove cart-drawer__remove" aria-label="Remove">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M7.99983 8.472L4.50249 11.9693C4.44027 12.0316 4.36383 12.0649 4.27316 12.0693C4.18249 12.0738 4.10161 12.0404 4.03049 11.9693C3.95938 11.8982 3.92383 11.8196 3.92383 11.7333C3.92383 11.6471 3.95938 11.5684 4.03049 11.4973L7.52783 8L4.03049 4.50267C3.96827 4.44045 3.93494 4.364 3.93049 4.27334C3.92605 4.18267 3.95938 4.10178 4.03049 4.03067C4.10161 3.95956 4.18027 3.924 4.26649 3.924C4.35272 3.924 4.43138 3.95956 4.50249 4.03067L7.99983 7.528L11.4972 4.03067C11.5594 3.96845 11.6361 3.93511 11.7272 3.93067C11.8174 3.92623 11.8981 3.95956 11.9692 4.03067C12.0403 4.10178 12.0758 4.18045 12.0758 4.26667C12.0758 4.35289 12.0403 4.43156 11.9692 4.50267L8.47183 8L11.9692 11.4973C12.0314 11.5596 12.0647 11.6362 12.0692 11.7273C12.0736 11.8176 12.0403 11.8982 11.9692 11.9693C11.8981 12.0404 11.8194 12.076 11.7332 12.076C11.6469 12.076 11.5683 12.0404 11.4972 11.9693L7.99983 8.472Z" fill="#00162B"/>
          </svg>
        </button>
        <div class="cart-drawer__image-wrapper">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.product_title}">
          </a>
        </div>
        <div class="cart-drawer__info">
          <a href="${item.url}" class="cart-drawer__title">${item.product_title.split("- trade")[0]}</a>
          ${variant}
          ${properties}
        </div>
        <div class="cart-drawer__quantity-wrapper">
          <div class="cart-drawer__prices">
            ${discounts}
            ${original}
            <span class="cart-drawer__price">${price}</span>
          </div>

          <div class="cart-drawer__quantity">
            <button type="button" class="qty minus" data-change="-${step}" aria-label="Decrease quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 8.33334V7.66667H12V8.33334H4Z" fill="#00162B"/>
              </svg>
            </button>
            
            <input type="number" class="qty-input" min="0" value="${item.quantity}" readonly>

            <button type="button" class="qty plus" data-change="${step}" aria-label="Increase quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.66667 8.33333H4V7.66667H7.66667V4H8.33333V7.66667H12V8.33333H8.33333V12H7.66667V8.33333Z" fill="#00162B"/>
              </svg>
            </button>
          </div>
        </div>
      </div>`;
  }

  bindLineItemEvents() {
    this.itemsContainer.querySelectorAll('.qty').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const lineItem = e.currentTarget.closest('.cart-drawer__line-item');
        const input = lineItem.querySelector('.qty-input');
        const delta = parseInt(e.currentTarget.dataset.change, 10);
        const newQty = Math.max(0, parseInt(input.value, 10) + delta);
        this.updateQuantity(lineItem.dataset.line, newQty);
      });
    });
    this.itemsContainer.querySelectorAll('.remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const lineItem = e.currentTarget.closest('.cart-drawer__line-item');
        document.dispatchEvent(new Event('cartDrawer:itemRemoved'));
        this.updateQuantity(lineItem.dataset.line, 0);
      });
    });
  }

  updateQuantity(line, quantity) {
    this.startLoading();
    fetch(`${cartRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity })
    })
      .then((r) => r.json())
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cartDrawer:quantityChange', { detail: cart }));
        document.dispatchEvent(new CustomEvent('cartDrawer:change', { detail: cart }));
        this.render(cart);
        this.stopLoading();
      })
      .catch(() => {
        this.showError('Unable to update cart');
        this.stopLoading();
      });
  }

  startLoading() {
    if (!this.panel.querySelector('.cart-drawer__loading')) {
      const loader = document.createElement('div');
      loader.className = 'cart-drawer__loading';
      loader.innerHTML = `<div class="fs-spinner"></div>`;
      this.panel.appendChild(loader);
    }
  }

  stopLoading() {
    const loader = this.panel.querySelector('.cart-drawer__loading');
    if (loader) loader.remove();
  }

  renderAddOn(cart) {
    if (!this.addOnData || !this.addOnContainer) return;
    const a = this.addOnData.a;
    const b = this.addOnData.b;
    const mode = this.addOnData.mode || 'auto';
    const inCart = cart.items.map((i) => i.product_id);
    let product = null;
    if (mode === 'always_a') product = a;
    else if (mode === 'always_b') product = b;
    else {
      const hasA = a && inCart.includes(a.id);
      const hasB = b && inCart.includes(b.id);
      if (hasA && hasB) product = null;
      else if (hasA && !hasB) product = b;
      else if (hasB && !hasA) product = a;
      else if (!hasA && !hasB) product = a;
    }
    if (!product) {
      this.addOnContainer.hidden = true;
      return;
    }
    this.addOnContainer.hidden = false;
    const variant = product.variants.find((v) => v.available);// || product.variants[0];
    if (!variant) return;
    this.addOnContainer.innerHTML = `
      <h3 class="cart-drawer__add-on-title">${this.addOnContainer.dataset.title || ''}</h3>
      <div class="cart-drawer__add-on-product" data-variant-id="${variant.id}">
        <div class="cart-drawer__add-on-image">
          <img src="${productImageSrc(product, variant)}" alt="${product.title}">
        </div>
        <div class="cart-drawer__add-on-info">
          <p>${product.title.split("- trade")[0]}</p>
          <p>${moneyFormat(variant.price)}</p>
        </div>
        <div class="cart-drawer__add-on-action">
          <button type="button" class="add-on-button">Add</button>
        </div>
      </div>`;
    document.dispatchEvent(new CustomEvent('cartDrawer:addOnShown', { detail: product }));
    this.addOnContainer.querySelector('.add-on-button').addEventListener('click', () => {
      this.startLoading();
      fetch(`${cartRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 })
      })
        .then(() => fetch(`${cartRoot}cart.js`))
        .then((r) => r.json())
        .then((newCart) => {
          document.dispatchEvent(new CustomEvent('cartDrawer:addOnAdded', { detail: newCart }));
          document.dispatchEvent(new CustomEvent('cartDrawer:change', { detail: newCart }));

          this.render(newCart);
          this.stopLoading();
        })
        .catch(() => {
          this.showError('Unable to add product');
          this.stopLoading();
        });
    });
  }

  renderEmptyUpsell() {
    if (!this.emptyUpsellBlock || !this.emptyUpsellContainer) return;
    if (!this.emptyUpsellData) {
      this.emptyUpsellBlock.hidden = true;
      return;
    }
    const product = this.emptyUpsellData;
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    this.emptyUpsellBlock.hidden = false;
    this.emptyUpsellContainer.innerHTML = `
      <div class="cart-drawer__add-on-product" data-variant-id="${variant.id}">
        <div class="cart-drawer__add-on-image">
          <img src="${productImageSrc(product, variant)}" alt="${product.title}">
        </div>
        <div class="cart-drawer__add-on-info">
          <p>${product.title.split("- trade")[0]}</p>
          <p>${moneyFormat(variant.price)}</p>

        </div>
        <div class="cart-drawer__add-on-action">
          <button type="button" class="add-on-button">Add</button>
        </div>
    </div>`;
  this.emptyUpsellContainer.querySelector('.add-on-button').addEventListener('click', () => {
    this.startLoading();
    fetch(`${cartRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variant.id, quantity: 1 })
    })
      .then(() => fetch(`${cartRoot}cart.js`))
        .then((r) => r.json())
        .then((newCart) => {
          this.render(newCart);
          this.stopLoading();
        })
        .catch(() => {
          this.showError('Unable to add product');
          this.stopLoading();
        });
    });
  }

  showError(message) {
    if (!this.messages) return;
    this.messages.textContent = message;
  }
}

const drawerElement = document.getElementById('CartDrawer');
const internal = drawerElement ? new CartDrawerInternal(drawerElement) : null;

window.CartDrawer = {
  open(cart) {
    internal && internal.open(cart);
  },
  close() {
    internal && internal.close();
  }
};

// Open the cart drawer when clicking the header basket icon
const cartDrawerTrigger = document.querySelector('a[aria-controls="CartDrawer"]');
if (cartDrawerTrigger && window.CartDrawer) {
  cartDrawerTrigger.addEventListener('click', (event) => {
    event.preventDefault();
    fetch(`${cartRoot}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        if(typeof evaluateCartRules == 'function') {
          evaluateCartRules(cart, function () {
            window.CartDrawer.open(cart);
          });
        } else {
          window.CartDrawer.open(cart);
        }
        //window.CartDrawer.open(cart);
      }).catch(() => {
        window.location.href = cartDrawerTrigger.getAttribute('href');
      });
  });
}
